import { createFileRoute, notFound } from "@tanstack/react-router"
import { getGoogleMapsSearchUrl } from "@/lib/utils/maps"
import { SITE_URL } from "@/lib/constants/site-url"
import {
  BAKERY_ADDRESS,
  SITE_NAME,
} from "@/lib/constants/site"
import { buildBakerySchema } from "@/lib/data/bakery-schema"
import VisitPage from "@/pages/visit"

const OG_IMAGE =
  `${SITE_URL}/images/plusbake/visit/storefront.webp`

export const Route = createFileRoute("/$countryCode/visit")({
  loader: async ({ params, context }) => {
    const { countryCode } = params
    const { queryClient } = context
    const { getRegion } = await import("@/lib/data/regions")

    const region = await queryClient.ensureQueryData({
      queryKey: ["region", countryCode],
      queryFn: () => getRegion({ country_code: countryCode }),
    })

    if (!region) {
      throw notFound()
    }

    return { countryCode, region }
  },
  head: () => {
    const title = "Hours & Directions | +Bake Cafe, Rancho Cucamonga"
    const description =
      "8685 Base Line Rd, Ste B in Sunrize Center. Open daily 7:30 AM–3:30 PM, kitchen until 2 PM. Free plaza parking, Wi-Fi, patio seating."
    const canonical = `${SITE_URL}/visit`
    const mapUrl = getGoogleMapsSearchUrl(
      encodeURIComponent(
        `${SITE_NAME} ${BAKERY_ADDRESS.street} ${BAKERY_ADDRESS.city} ${BAKERY_ADDRESS.region} ${BAKERY_ADDRESS.postalCode}`
      )
    )

    const restaurantSchema = buildBakerySchema()

    const placeSchema = {
      "@context": "https://schema.org",
      "@type": "Place",
      name: SITE_NAME,
      hasMap: mapUrl,
      address: restaurantSchema.address,
      geo: restaurantSchema.geo,
    }

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Visit", item: canonical },
      ],
    }

    return {
      meta: [
        { title },
        { name: "description", content: description },
        {
          name: "keywords",
          content:
            "Rancho Cucamonga bakery, Asian-fusion bakery Rancho Cucamonga, bakery near Upland, bakery near Ontario, salt bread Rancho Cucamonga, pandan latte, matcha Rancho Cucamonga",
        },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        { property: "og:image", content: OG_IMAGE },
        { property: "og:site_name", content: SITE_NAME },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(restaurantSchema) },
        { type: "application/ld+json", children: JSON.stringify(placeSchema) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema) },
      ],
    }
  },
  component: VisitPage,
})
